import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AutoCompleteList from "components/AutoCompleteList";
import React, { useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useSelector } from "react-redux";
import { dispatch } from "store";
import {
  getProductPriceInMachineThunk,
  getProductsInMachineThunk,
} from "store/reducers/statistics/statisticsSlice";
import { getMinMachinesThunk } from "store/reducers/vending";
import addURLPrefix from "utils/addURLPrefix";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import useDownloader from "react-use-downloader";
import { baseUrl } from "data/server_apis/server_api";
import BottomDialog from "components/BottomDialog";
import CloseIcon from "@mui/icons-material/Close";

const ProductsInMachine = () => {
  const [machine, setMachine] = React.useState(null);
  const [page, setPage] = React.useState(1);
  useEffect(() => {
    dispatch(getProductsInMachineThunk({ machine: machine?.id, page }));
  }, [machine, page]);

  useEffect(() => {
    dispatch(getMinMachinesThunk());
  }, []);

  useEffect(() => {
    dispatch(getProductPriceInMachineThunk(machine?.id));
  }, [machine]);

  const statistics = useSelector((state) => state.statistics);

  const machines = useSelector((state) => state.vending.minMachines);

  const token = useSelector((state) => state.auth?.user?.access);
  const [openDownload, setOpenDownload] = useState(false);

  const { elapsed, download, cancel, error, isInProgress } = useDownloader({
    mode: "cors",
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const startDownload = () => {
    setOpenDownload(true);
    const now = new Date();
    const timestamp = now.toISOString().replace(/[-:.]/g, "");
    const filename = `${timestamp}-export.csv`;
    download(
      `${baseUrl}/commodity/product-arrangement/product_analytics_csv_export?machine=${machine?.id ?? ""}`,
      filename
    );
  };
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  return (
    <Stack direction="column" justifyContent="space-between" padding={2}>
      <Typography variant="h6" sx={{ color: "red", width: "100%", mb: 1 }}>
        ** Product price is not inclusive of any offers or discounts.
      </Typography>
      <Box>
        <Grid container spacing={1} sx={{ pr: 3 ,mb: 1}}>
          <Grid item lg={4} md={6} xs={12}>
            <Stack
              alignItems="end"
              direction="row"
              spacing={2}
              marginBottom={2}
            >
              <AutoCompleteList
                items={machines}
                name="name"
                label="Filter by machine"
                setValue={(value) => {
                  setMachine(value);
                }}
                value={machine}
              />
            </Stack>
          </Grid>
          <Grid item lg={4} md={6} xs={12}>
            <Stack sx={{ padding: 1 }} direction="row" spacing={2}>
              <Typography variant="h5">
                Total value : {statistics?.productPriceInMachine} Rs
              </Typography>
            </Stack>
          </Grid>
          <Grid item lg={4} md={6} xs={12}>
            <Stack justifyContent="end" alignItems={ isSmallScreen ?"start":"end"}>
              <Button
                startIcon={<FileDownloadIcon />}
                variant="outlined"
                onClick={startDownload}
                
              >
                Export CSV
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {statistics?.productsInMachineStatus?.loading === "get" ? (
          <Grid container spacing={1}>
            <Grid item xl={1} lg={2} md={3} xs={6}>
              <Paper
                elevation={2}
                sx={{
                  height: 220,
                  cursor: "pointer",
                  borderRadius: 2,
                }}
              >
                <Skeleton
                  variant="rectangular"
                  sx={{ height: 220 }}
                  animation="wave"
                />
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={1}>
            {statistics?.productsInMachine?.map((product) => (
              <Grid item xl={1} lg={2} md={3} xs={6} key={product.id}>
                <Paper
                  elevation={2}
                  sx={{
                    height: 240,
                    cursor: "pointer",
                    borderRadius: 2,
                  }}
                >
                  <Stack direction="column">
                    <LazyLoadImage
                      style={{
                        objectFit: "contain",
                        height: 140,
                        overflow: "hidden",
                        borderTopLeftRadius: 8,
                        borderBottomLeftRadius: 8,
                      }}
                      alt=""
                      src={addURLPrefix(product.images[0]?.image)}
                    />
                    <Divider />
                    <Stack
                      direction="column"
                      flexGrow={1}
                      paddingLeft={1}
                      sx={{ padding: 1 }}
                    >
                      <Tooltip title={product.name}>
                        <Typography
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                            flexGrow: 1,
                          }}
                          variant="h5"
                        >
                          {product.name}
                        </Typography>
                      </Tooltip>
                      <Typography variant="h6" sx={{ color: "grey" }}>
                        Count : {product.qty}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "grey" }}>
                        Price : {product.price.toFixed(2)} Rs
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "grey" }}>
                        Total : {(product.qty * product.price).toFixed(2)} Rs
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
        <Stack direction="row" justifyContent="center" marginTop={2}>
          <Pagination
            count={statistics.productsInMachinePageSize}
            variant="outlined"
            shape="rounded"
            onChange={(event, value) => {
              setPage(value);
            }}
          />
        </Stack>
      </Box>
      <BottomDialog
        open={isInProgress}
        onClose={() => {
          setOpenDownload(false);
        }}
      >
        <Stack direction="column">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5" sx={{ ml: 2 }}>
              Downloading invoice
            </Typography>
            <IconButton
              onClick={() => {
                setOpenDownload(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <Box sx={{ m: 2 }}>
            <LinearProgress />
          </Box>
          <Typography variant="caption" sx={{ mx: 2 }}>
            Time elapsed: {elapsed} Seconds
          </Typography>
          <Stack direction="row">
            <Box sx={{ ml: 2, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={cancel}
              >
                Cancel download
              </Button>
            </Box>
            {error && (
              <Typography variant="subtitle1" color="red">
                Error occurred
              </Typography>
            )}
          </Stack>
        </Stack>
      </BottomDialog>
    </Stack>
  );
};

export default ProductsInMachine;
